
import React, { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Database, Search, Download, Mail, Send, Paperclip, X } from "lucide-react";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { sendEmail, fileToAttachment, type EmailAttachment } from "@/services/emailService";

// Mock customer data
const CUSTOMERS = [
  { id: 1, name: "Thomas Schmidt", email: "t.schmidt@example.com", phone: "+49 123 4567890", registeredAt: "2023-08-15", status: "Aktiv" },
  { id: 2, name: "Marie Weber", email: "m.weber@example.com", phone: "+49 123 4567891", registeredAt: "2023-09-22", status: "Aktiv" },
  { id: 3, name: "Andreas Müller", email: "a.mueller@example.com", phone: "+49 123 4567892", registeredAt: "2023-10-05", status: "Inaktiv" },
  { id: 4, name: "Sabine Wagner", email: "s.wagner@example.com", phone: "+49 123 4567893", registeredAt: "2023-11-11", status: "Aktiv" },
  { id: 5, name: "Markus Fischer", email: "m.fischer@example.com", phone: "+49 123 4567894", registeredAt: "2023-12-02", status: "Aktiv" },
];

const CustomerData = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailReplyTo, setEmailReplyTo] = useState("");
  const [attachments, setAttachments] = useState<EmailAttachment[]>([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [isSending, setIsSending] = useState(false);
  
  const filteredCustomers = CUSTOMERS.filter(customer => 
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    customer.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openEmailModal = (customer) => {
    setSelectedCustomer(customer);
    setEmailSubject("");
    setEmailBody("");
    setEmailReplyTo("");
    setAttachments([]);
    setEmailModalOpen(true);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      setUploadingFile(true);
      
      // Maximale Dateigröße: 10MB
      const file = files[0];
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Die Datei ist zu groß. Maximale Größe: 10MB");
        return;
      }
      
      const attachment = await fileToAttachment(file);
      setAttachments(prev => [...prev, attachment]);
      
      // Formular zurücksetzen
      e.target.value = '';
    } catch (error) {
      toast.error("Fehler beim Hochladen der Datei: " + error.message);
    } finally {
      setUploadingFile(false);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSendEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) {
      toast.error("Bitte füllen Sie Betreff und Nachricht aus");
      return;
    }

    setIsSending(true);
    
    try {
      // E-Mail über den EmailService senden
      const result = await sendEmail({
        to: selectedCustomer.email,
        subject: emailSubject,
        body: emailBody,
        replyTo: emailReplyTo || undefined,
        attachments: attachments.length > 0 ? attachments : undefined
      });
      
      if (result.success) {
        toast.success(`E-Mail an ${selectedCustomer.name} wurde erfolgreich gesendet`);
        setEmailModalOpen(false);
        setSelectedCustomer(null);
        setEmailSubject("");
        setEmailBody("");
        setEmailReplyTo("");
        setAttachments([]);
      } else {
        toast.error(result.message || "Fehler beim Senden der E-Mail");
      }
    } catch (error) {
      toast.error("Fehler beim Senden der E-Mail. Bitte versuchen Sie es später erneut.");
      console.error("Error sending email:", error);
    } finally {
      setIsSending(false);
    }
  };
  
  return (
    <AdminLayout>
      <div className="p-6 animate-fade-in">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <div>
            <h1 className="text-2xl font-semibold mb-2 flex items-center">
              <Database className="mr-3 text-[#41c7f0]" size={24} />
              Kundendaten
            </h1>
            <p className="text-gray-400">Verwaltung und Einsicht aller Kundendaten</p>
          </div>
          
          <div className="flex gap-3 mt-4 md:mt-0">
            <Button variant="outline" className="flex items-center">
              <Download size={16} className="mr-2" />
              Exportieren
            </Button>
          </div>
        </div>
        
        <div className="bg-[#0c0c0c] border border-gray-800/30 rounded-xl p-6 shadow-lg mb-6">
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <Input
              className="pl-10 bg-[#151515] border-gray-700"
              placeholder="Suche nach Namen oder E-Mail..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>E-Mail</TableHead>
                  <TableHead>Telefon</TableHead>
                  <TableHead>Registriert am</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCustomers.map((customer) => (
                  <TableRow key={customer.id}>
                    <TableCell className="font-mono">{customer.id}</TableCell>
                    <TableCell>{customer.name}</TableCell>
                    <TableCell>{customer.email}</TableCell>
                    <TableCell>{customer.phone}</TableCell>
                    <TableCell>{customer.registeredAt}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        customer.status === "Aktiv" 
                          ? "bg-green-500/20 text-green-500" 
                          : "bg-yellow-500/20 text-yellow-500"
                      }`}>
                        {customer.status}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => openEmailModal(customer)}
                        title="E-Mail senden"
                      >
                        <Mail size={16} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>E-Mail senden</DialogTitle>
              <DialogDescription>
                {selectedCustomer && (
                  <span>Senden Sie eine E-Mail an {selectedCustomer.name} ({selectedCustomer.email})</span>
                )}
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <label htmlFor="sender-email" className="text-sm font-medium">
                    Absender
                  </label>
                  <Input
                    id="sender-email"
                    value="work@hoss-immo.de"
                    disabled
                    className="bg-muted"
                  />
                </div>
                
                <div className="grid gap-2">
                  <label htmlFor="reply-to" className="text-sm font-medium">
                    Antwort an (optional)
                  </label>
                  <Input
                    id="reply-to"
                    type="email"
                    value={emailReplyTo}
                    onChange={(e) => setEmailReplyTo(e.target.value)}
                    placeholder="ihre-email@example.com"
                  />
                </div>
              </div>
              
              <div className="grid gap-2">
                <label htmlFor="subject" className="text-sm font-medium">
                  Betreff
                </label>
                <Input
                  id="subject"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Geben Sie einen Betreff ein"
                />
              </div>
              
              <div className="grid gap-2">
                <label htmlFor="message" className="text-sm font-medium">
                  Nachricht
                </label>
                <Textarea
                  id="message"
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  placeholder="Geben Sie Ihre Nachricht ein"
                  rows={8}
                />
              </div>
              
              <div className="grid gap-2">
                <label className="text-sm font-medium">
                  Anhänge
                </label>
                
                <div className="flex flex-wrap gap-2 mb-2">
                  {attachments.map((attachment, index) => (
                    <div key={index} className="bg-gray-800 text-sm rounded-lg px-3 py-1 flex items-center gap-2">
                      <Paperclip size={14} />
                      <span className="truncate max-w-[200px]">{attachment.filename}</span>
                      <button 
                        type="button" 
                        onClick={() => removeAttachment(index)}
                        className="hover:text-red-400"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
                
                <div className="flex items-center">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => document.getElementById('file-upload')?.click()}
                    disabled={uploadingFile}
                    className="gap-2"
                  >
                    <Paperclip size={16} />
                    {uploadingFile ? 'Wird hochgeladen...' : 'Datei anhängen'}
                  </Button>
                  <input
                    id="file-upload"
                    type="file"
                    className="hidden"
                    onChange={handleFileUpload}
                    accept="application/pdf,image/jpeg,image/png,image/gif,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  />
                  <span className="ml-4 text-xs text-gray-400">Max. 10MB pro Datei</span>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEmailModalOpen(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleSendEmail} disabled={isSending} className="gap-2">
                {isSending ? (
                  <>Wird gesendet...</>
                ) : (
                  <>
                    <Send size={16} />
                    Senden
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default CustomerData;
